import { API_PROXY_TARGET } from '../../apiConfig';
import QRCode from 'qrcode';
import sanitizeHtml from '../../helpers/sanitizeHtml';

export const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const cleanText = value =>
  String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const toFileSafeName = value =>
  String(value || 'analysis-result')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'analysis-result';

export const resolveFilialLogoSrc = logo => {
  const rawValue = String(logo || '').trim();
  if (!rawValue) {
    return '';
  }

  if (/^(https?:)?\/\//i.test(rawValue) || rawValue.startsWith('data:')) {
    return rawValue;
  }

  if (rawValue.includes('userfiles/images/filial/')) {
    return `${API_PROXY_TARGET}/${rawValue.replace(/^\/+/, '')}`;
  }

  return `${API_PROXY_TARGET}/userfiles/images/filial/${rawValue.replace(/^\/+/, '')}`;
};

export const fetchAssetAsDataUrl = async assetUrl => {
  if (!assetUrl) {
    return '';
  }

  const response = await fetch(assetUrl, { mode: 'cors', credentials: 'include' });
  if (!response.ok) {
    throw new Error('Logo yuklanmadi');
  }

  const blob = await response.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Logo yuklanmadi'));
    reader.readAsDataURL(blob);
  });
};

export const createAnalysisDocumentKey = (patientId, analysisId) => {
  const source = `${patientId}|${analysisId}|suvoriy-analysis`;
  let hash = 2166136261;

  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return Math.abs(hash >>> 0).toString(36);
};

export const buildAnalysisDocumentUrl = (patientId, analysisId, origin = '') => {
  const key = createAnalysisDocumentKey(patientId, analysisId);
  const baseOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : '');

  return `${baseOrigin}/analysis-document?patient_id=${patientId}&analysis_id=${analysisId}&key=${key}`;
};

export const buildAnalysisQrDataUrl = async value =>
  QRCode.toDataURL(String(value || 'suvoriy-analysis'), {
    width: 180,
    margin: 1,
    color: {
      dark: '#111111',
      light: '#FFFFFF'
    }
  });

export const getAnalysisNormLabels = (item, draft) =>
  (item.norm_options || [])
    .filter(norm => (draft.norm_ids || []).includes(Number(norm.id)))
    .map(norm => norm.norm)
    .filter(Boolean);

export const getAnalysisMethodTitle = (item, draft) => {
  const selectedMethod = (item.methods || []).find(method => String(method.id) === String(draft.method_id || ''));
  return selectedMethod?.title || item.method_title || "Bo'lim ko'rsatilmagan";
};

export const buildAnalysisDocument = ({
  patient,
  item,
  draft,
  filial,
  statusLabel,
  qrSrc,
  specialistName,
  doctorName
}) => {
  const normLabels = getAnalysisNormLabels(item, draft);
  const methodTitle = getAnalysisMethodTitle(item, draft);
  const rawResult = draft.result || item.result || '';
  const formattedDate = item.saved_at || item.date || new Date().toISOString().slice(0, 10);
  const companyName = cleanText(filial?.brand_text) || patient.filial_name || 'Suvoriy Medical Center';
  const logoSrc = resolveFilialLogoSrc(filial?.logo);
  const certificateNumber = `${String(patient.id).padStart(6, '0')}-${String(item.id).padStart(6, '0')}`;
  const resultHtml =
    Number(item.result_type_value) !== 1
      ? sanitizeHtml(rawResult || '<p>Natija hali kiritilmagan</p>')
      : `<p>${escapeHtml(rawResult || 'Natija hali kiritilmagan')}</p>`;
  const styles = `
    * { box-sizing: border-box; }
    html, body { width: 210mm; min-height: 297mm; }
    html, body, .med-doc-page, .med-doc-sheet, .med-doc-status, .med-doc-chip, .med-doc-qr img {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #000000;
      font-family: Roboto, Arial, sans-serif;
    }
    .med-doc-page {
      width: 210mm;
      min-height: 297mm;
      padding: 4mm;
      background: #ffffff;
    }
    .med-doc-sheet {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-height: 289mm;
      padding: 5mm 6mm 5mm;
      border: 1px solid #000000;
      background: #ffffff;
    }
    .med-doc-head {
      display: grid;
      grid-template-columns: 20mm minmax(0, 1fr) 22mm;
      gap: 4mm;
      align-items: start;
      margin-bottom: 3mm;
    }
    .med-doc-logo {
      width: 18mm;
      height: 18mm;
      border: 1px solid #000000;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      background: #ffffff;
    }
    .med-doc-logo img {
      display: block;
      width: 14mm;
      height: 14mm;
      object-fit: contain;
    }
    .med-doc-logo-fallback {
      font-size: 7px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      text-align: center;
      line-height: 1.35;
    }
    .med-doc-title {
      text-align: center;
    }
    .med-doc-title small {
      display: block;
      margin-bottom: 1mm;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #000000;
    }
    .med-doc-title h1 {
      margin: 0;
      font-size: 20px;
      line-height: 1.05;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .med-doc-title p {
      margin: 1.2mm 0 0;
      font-size: 11px;
      line-height: 1.35;
      font-weight: 500;
    }
    .med-doc-qr {
      display: flex;
      justify-content: flex-end;
    }
    .med-doc-qr img {
      display: block;
      width: 18mm;
      height: 18mm;
      border: 1px solid #000000;
      padding: 0.8mm;
      background: #ffffff;
    }
    .med-doc-list {
      margin-bottom: 3mm;
      border-top: 1px solid #000000;
      border-left: 1px solid #000000;
    }
    .med-doc-row {
      display: grid;
      grid-template-columns: 34mm minmax(0, 1fr);
      border-right: 1px solid #000000;
      border-bottom: 1px solid #000000;
      align-items: start;
    }
    .med-doc-label {
      padding: 1.8mm 2.2mm;
      border-right: 1px solid #000000;
      font-size: 10.5px;
      font-weight: 700;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .med-doc-value {
      padding: 1.8mm 2.4mm;
      font-size: 11.5px;
      line-height: 1.35;
      font-weight: 400;
      word-break: break-word;
    }
    .med-doc-status {
      display: inline-block;
      padding: 0.8mm 2mm;
      border: 1px solid #000000;
      font-size: 9.5px;
      font-weight: 700;
      text-transform: uppercase;
    }
    .med-doc-result {
      margin-top: 2mm;
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
    }
    .med-doc-result h2 {
      margin: 0 0 1.4mm;
      font-size: 14px;
      line-height: 1.25;
      font-weight: 700;
      text-transform: uppercase;
    }
    .med-doc-result-content {
      flex: 1 1 auto;
      min-height: 0;
      font-size: 12px;
      line-height: 1.35;
      color: #000000;
      overflow: hidden;
    }
    .med-doc-result-content p {
      margin: 0 0 1.2mm;
    }
    .med-doc-result-content p:last-child {
      margin-bottom: 0;
    }
    .med-doc-result-content table {
      width: 100%;
      margin: 1.6mm 0;
      border-collapse: collapse;
      font-size: 11px;
    }
    .med-doc-result-content td,
    .med-doc-result-content th {
      border: 1px solid #000000;
      padding: 1.2mm 1.6mm;
      vertical-align: top;
    }
    .med-doc-norms {
      display: flex;
      flex-wrap: wrap;
      gap: 1mm;
      margin-top: 2mm;
    }
    .med-doc-chip {
      display: inline-flex;
      align-items: center;
      padding: 0.7mm 1.6mm;
      border: 1px solid #000000;
      font-size: 9.5px;
      font-weight: 700;
    }
    .med-doc-people {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 5mm;
      margin-top: 3mm;
      padding-top: 2mm;
      border-top: 1px solid #000000;
    }
    .med-doc-person strong {
      display: block;
      margin-bottom: 0.8mm;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
    }
    .med-doc-person span {
      display: block;
      font-size: 11.5px;
      line-height: 1.35;
    }
    @page {
      size: A4 portrait;
      margin: 0;
    }
  `;

  const markup = `
    <div class="med-doc-page">
      <div class="med-doc-sheet">
        <div class="med-doc-head">
          <div class="med-doc-logo">
            ${
              logoSrc
                ? `<img src="${escapeHtml(logoSrc)}" alt="${escapeHtml(companyName)}" />`
                : `<div class="med-doc-logo-fallback">Suvoriy<br/>Lab</div>`
            }
          </div>
          <div class="med-doc-title">
            <small>Laboratoriya natijalarini tasdiqlash hujjati</small>
            <h1>Analiz Natijasi</h1>
            <p>${escapeHtml(companyName)}</p>
          </div>
          <div class="med-doc-qr">
            <img src="${qrSrc}" alt="QR code" />
          </div>
        </div>

        <div class="med-doc-list">
          <div class="med-doc-row">
            <div class="med-doc-label">Reestr</div>
            <div class="med-doc-value">${escapeHtml(certificateNumber)}</div>
          </div>
          <div class="med-doc-row">
            <div class="med-doc-label">Bemor</div>
            <div class="med-doc-value">${escapeHtml(patient.full_name || '-')}</div>
          </div>
          <div class="med-doc-row">
            <div class="med-doc-label">Bemor ID</div>
            <div class="med-doc-value">${escapeHtml(patient.id || '-')}</div>
          </div>
          <div class="med-doc-row">
            <div class="med-doc-label">Sana</div>
            <div class="med-doc-value">${escapeHtml(formattedDate)}</div>
          </div>
          <div class="med-doc-row">
            <div class="med-doc-label">Analiz</div>
            <div class="med-doc-value">${escapeHtml(item.analysis_title || 'Analiz')}</div>
          </div>
          <div class="med-doc-row">
            <div class="med-doc-label">Bo'lim / metod</div>
            <div class="med-doc-value">${escapeHtml(methodTitle)}</div>
          </div>
          <div class="med-doc-row">
            <div class="med-doc-label">Holat</div>
            <div class="med-doc-value"><span class="med-doc-status">${escapeHtml(statusLabel)}</span></div>
          </div>
          ${
            normLabels.length > 0
              ? `
                <div class="med-doc-row">
                  <div class="med-doc-label">Norma</div>
                  <div class="med-doc-value">
                    <div class="med-doc-norms">
                      ${normLabels.map(label => `<span class="med-doc-chip">${escapeHtml(label)}</span>`).join('')}
                    </div>
                  </div>
                </div>
              `
              : ''
          }
        </div>

        <div class="med-doc-result">
          <h2>Natija</h2>
          <div class="med-doc-result-content">${resultHtml}</div>
        </div>

        <div class="med-doc-people">
          <div class="med-doc-person">
            <strong>Laboratoriya mutaxassisi</strong>
            <span>${escapeHtml(specialistName || 'Ko‘rsatilmagan')}</span>
          </div>
          <div class="med-doc-person">
            <strong>Mas'ul shifokor</strong>
            <span>${escapeHtml(doctorName || patient.doctor || 'Ko‘rsatilmagan')}</span>
          </div>
        </div>
      </div>
    </div>
  `;

  return {
    title: item.analysis_title || 'Analiz natijasi',
    styles,
    markup
  };
};

export const buildAnalysisExportHtml = params => {
  const documentPayload = buildAnalysisDocument(params);

  return `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(documentPayload.title)}</title>
        <style>${documentPayload.styles}</style>
      </head>
      <body>${documentPayload.markup}</body>
    </html>
  `;
};
